'use client';

import { cn } from '@insta-club/utilities';
import { motion } from 'framer-motion';
import React from 'react';

export interface Props {
    percentage: number;
    size?: number;
    strokeWidth?: number;
    className?: string;
    trackColor?: string;
    textColor?: string;
    barColor?: string;
    duration?: number;
    spring?: boolean;
    showText?: boolean;
}

const CircularProgress: React.FC<Props> = ({
    percentage = 0,
    size = 80,
    strokeWidth = 6,
    className,
    trackColor = 'text-gray-300',
    barColor = 'text-primary-500',
    textColor = 'text-gray-600',
    duration = 0.5,
    spring = false,
    showText = true
}) => {
    const safeSize = Number(size) || 80;
    const safeStroke = Number(strokeWidth) || 6;
    const safePercent = Math.min(Math.max(Number(percentage) || 0, 0), 100);

    const radius = (safeSize - safeStroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (safePercent / 100) * circumference;
    const roundSafePercent = Math.round(safePercent);

    return (
        <div
            className={cn('relative inline-flex items-center justify-center', className)}
            style={{ width: safeSize, height: safeSize }}
        >
            <svg className='-rotate-90 transform' width={safeSize} height={safeSize}>
                <circle
                    cx={safeSize / 2}
                    cy={safeSize / 2}
                    r={radius}
                    stroke='currentColor'
                    strokeWidth={safeStroke}
                    className={trackColor}
                    fill='transparent'
                />

                <motion.circle
                    cx={safeSize / 2}
                    cy={safeSize / 2}
                    r={radius}
                    stroke='currentColor'
                    strokeWidth={safeStroke}
                    strokeLinecap='round'
                    className={barColor}
                    fill='transparent'
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    animate={{
                        strokeDashoffset: circumference - (safePercent / 100) * circumference
                    }}
                    transition={
                        spring ? { type: 'spring', stiffness: 120, damping: 18 } : { duration, ease: 'easeInOut' }
                    }
                />
            </svg>

            {showText && (
                <span
                    className={cn('absolute text-[10px] font-semibold select-none', textColor)}
                    style={{ fontFamily: 'Poppins' }}
                >
                    {roundSafePercent > 0 ? `${Math.round(safePercent)}%` : '-'}
                </span>
            )}
        </div>
    );
};

CircularProgress.displayName = 'CircularProgress';

export default CircularProgress;

